/** P32 (Feb 27, 2026) — Agency Identity tab.
 *
 * Edits the EDI 837P Billing Provider (Loop 2010AA) data for a
 * selected agency. The orchestrator page picks the agency + manages
 * `agencyForm` state; this component handles the form layout, save,
 * and CODB info-card.
 */
import React from 'react';
import {
  ArrowsClockwise, Buildings, IdentificationCard, ShieldCheck,
} from '@phosphor-icons/react';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

import { TAXONOMY_PRESETS } from './sharedUi';

const AgencyIdentityTab = ({
  agencies, selectedAgencyId, setSelectedAgencyId,
  agencyForm, setAgencyForm, saving, onSave, onReload,
}) => (
  <Card className="border-slate-200">
    <CardHeader>
      <div className="flex items-start justify-between">
        <div>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Buildings size={20} /> Agency Identity
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Edits the EDI 837P Billing Provider (Loop 2010AA) data for the selected agency.
          </p>
        </div>
        <Select value={selectedAgencyId} onValueChange={setSelectedAgencyId}>
          <SelectTrigger className="w-72" data-testid="agency-picker">
            <SelectValue placeholder="Pick an agency..." />
          </SelectTrigger>
          <SelectContent>
            {agencies.map((a) => (
              <SelectItem key={a.id} value={a.id} data-testid={`agency-option-${a.id}`}>
                {a.name} {a.status !== 'active' ? <span className="ml-2 text-xs text-slate-400">({a.status})</span> : null}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </CardHeader>
    <CardContent className="space-y-6">
      {!agencyForm ? (
        <p className="text-sm text-slate-500 italic">Select an agency to edit its settings.</p>
      ) : (
        <>
          {/* Core identity */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="agency-name">Agency Name</Label>
              <Input
                id="agency-name"
                value={agencyForm.name}
                onChange={(e) => setAgencyForm({ ...agencyForm, name: e.target.value })}
                data-testid="agency-name-input"
              />
            </div>
            <div>
              <Label htmlFor="agency-npi">NPI (10 digits) <span className="text-xs text-slate-400 ml-1">PRV*BI emit, Loop 2010AA</span></Label>
              <Input
                id="agency-npi"
                value={agencyForm.npi}
                maxLength={10}
                onChange={(e) => setAgencyForm({ ...agencyForm, npi: e.target.value.replace(/\D/g, '') })}
                className="font-mono"
                data-testid="agency-npi-input"
              />
            </div>
            <div>
              <Label htmlFor="agency-tax-id">Tax ID (EIN)</Label>
              <Input
                id="agency-tax-id"
                value={agencyForm.tax_id}
                onChange={(e) => setAgencyForm({ ...agencyForm, tax_id: e.target.value })}
                placeholder="XX-XXXXXXX"
                className="font-mono"
                data-testid="agency-tax-id-input"
              />
            </div>
            <div>
              <Label htmlFor="agency-taxonomy">Taxonomy Code <span className="text-xs text-slate-400 ml-1">PRV*BI*PXC</span></Label>
              <div className="flex gap-2">
                <Input
                  id="agency-taxonomy"
                  value={agencyForm.taxonomy_code}
                  onChange={(e) => setAgencyForm({ ...agencyForm, taxonomy_code: e.target.value.toUpperCase() })}
                  className="font-mono"
                  data-testid="agency-taxonomy-input"
                />
                <Select
                  value=""
                  onValueChange={(v) => setAgencyForm({ ...agencyForm, taxonomy_code: v })}
                >
                  <SelectTrigger className="w-12" data-testid="agency-taxonomy-presets">
                    <SelectValue placeholder="…" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAXONOMY_PRESETS.map((t) => (
                      <SelectItem key={t.code} value={t.code} data-testid={`taxonomy-preset-${t.code}`}>
                        {t.code} — {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Medicaid / DODD Contracts */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <IdentificationCard size={16} /> Medicaid / DODD Contracts
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="medicaid-provider-id">Medicaid Provider #</Label>
                <Input
                  id="medicaid-provider-id"
                  value={agencyForm.medicaid_provider_id}
                  onChange={(e) => setAgencyForm({ ...agencyForm, medicaid_provider_id: e.target.value })}
                  placeholder="0468986"
                  className="font-mono"
                  data-testid="medicaid-provider-id-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">EDI 837P REF*G2 segment.</p>
              </div>
              <div>
                <Label htmlFor="dodd-contract-number">DODD Contract #</Label>
                <Input
                  id="dodd-contract-number"
                  value={agencyForm.dodd_contract_number}
                  onChange={(e) => setAgencyForm({ ...agencyForm, dodd_contract_number: e.target.value })}
                  placeholder="2573651"
                  className="font-mono"
                  data-testid="dodd-contract-number-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">7-digit DODD contract identifier (optional).</p>
              </div>
            </div>
            <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-md p-3 text-xs text-indigo-900 flex items-start gap-2" data-testid="codb-info-note">
              <ShieldCheck size={16} className="mt-0.5 shrink-0" />
              <div>
                <strong>CODB tier is per-client.</strong> The DODD rate matrix (OAC 5123-9-14 Appendix C) maps each of Ohio&apos;s 88 counties to a CODB tier 1–8. When you submit DODD billing, each claim is priced using the tier resolved from the patient&apos;s county on their profile.{' '}
                <a href="/patients" className="underline">Edit patient counties →</a>
              </div>
            </div>
          </div>

          {/* Business Address */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Business Address</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label>Street</Label>
                <Input
                  value={agencyForm.address.street1}
                  onChange={(e) => setAgencyForm({ ...agencyForm, address: { ...agencyForm.address, street1: e.target.value } })}
                  data-testid="agency-address-street-input"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={agencyForm.address.city}
                  onChange={(e) => setAgencyForm({ ...agencyForm, address: { ...agencyForm.address, city: e.target.value } })}
                  data-testid="agency-address-city-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>State</Label>
                  <Input
                    value={agencyForm.address.state}
                    onChange={(e) => setAgencyForm({ ...agencyForm, address: { ...agencyForm.address, state: e.target.value.toUpperCase() } })}
                    maxLength={2}
                    data-testid="agency-address-state-input"
                  />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input
                    value={agencyForm.address.zip_code}
                    onChange={(e) => setAgencyForm({ ...agencyForm, address: { ...agencyForm.address, zip_code: e.target.value } })}
                    data-testid="agency-address-zip-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Billing Contact */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Billing Contact</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={agencyForm.contact.name}
                  onChange={(e) => setAgencyForm({ ...agencyForm, contact: { ...agencyForm.contact, name: e.target.value } })}
                  data-testid="agency-contact-name-input"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={agencyForm.contact.phone}
                  onChange={(e) => setAgencyForm({ ...agencyForm, contact: { ...agencyForm.contact, phone: e.target.value } })}
                  data-testid="agency-contact-phone-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={agencyForm.contact.email}
                  type="email"
                  onChange={(e) => setAgencyForm({ ...agencyForm, contact: { ...agencyForm.contact, email: e.target.value } })}
                  data-testid="agency-contact-email-input"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onReload} data-testid="reload-agency-btn">
              <ArrowsClockwise size={16} className="mr-2" /> Reload
            </Button>
            <Button onClick={onSave} disabled={saving} className="bg-indigo-900 hover:bg-indigo-800" data-testid="save-agency-btn">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </>
      )}
    </CardContent>
  </Card>
);

export default AgencyIdentityTab;
